import { cloneDeep, pick } from 'es-toolkit'
import type { AbstractId } from '#shared/types/utils'
import { useApi } from '~/composables/useApi'
import type { FetchOptions } from 'ofetch'

/**
 * Overwrites the keys of modelA with the values from modelB.
 * If useKeys is present, this function will only overwrite those keys instead.
 * Warning: this function replaces modelA in place and also returns the result.
 * If you want modelA to not be updated, use `updateModelWithClone` instead.
 *
 * Example:
 *
 * modelA: id, firstName, comments
 *
 * modelB: id, firstName, frequency
 *
 * updateModel(modelA, modelB)
 * -> { id: modelB, firstName: modelB, comments: modelA }
 *
 * updateModel(modelA, modelB, ['firstName']
 * -> { id: modelA, firstName: modelB, comments: modelA }
 * @param modelA
 * @param modelB
 * @param useKeys
 */
export function updateModel<T extends object | undefined | null>(
  modelA: T,
  modelB: Partial<Record<keyof T, any>>,
  useKeys: (keyof T)[] | undefined = undefined
): T {
  if (!modelA) {
    return modelA
  }

  if (useKeys) {
    const modelBRemovedKeys = pick(modelB, useKeys)
    return Object.assign(modelA, modelBRemovedKeys)
  } else {
    return Object.assign(modelA, pick(modelB, Object.keys(modelA) as (keyof T)[]))
  }
}

export function updateModelWithClone<T extends object>(
  modelA: T,
  modelB: Partial<Record<keyof T, any>>,
  useKeys: (keyof T)[] | undefined = undefined
): T {
  return updateModel(cloneDeep(modelA), modelB, useKeys)
}

export function removeItemFromList<T extends { id: AbstractId<string> }>(
  list: T[] | undefined,
  item: T
) {
  if (!list) {
    return list
  }

  const eventIndex = list.findIndex((model) => model.id === item.id)

  if (eventIndex !== -1) {
    list.splice(eventIndex, 1)
  }
  return list
}

export function removeItemFromListWithClone<T extends { id: AbstractId<string> }>(
  list: T[] | undefined,
  item: T
) {
  if (!list) {
    return list
  }
  return removeItemFromList(cloneDeep(list), item)
}

type NoResponseFetchProxy = {
  [K in 'get' | 'post' | 'put' | 'delete' | 'patch']: (params: {
    url: string
    payload?: any
    success?: MaybeRefOrGetter<string | undefined>
    error?: string | ((error: any) => string | undefined)
    config?: FetchOptions
  }) => Promise<boolean>
}

export const noResponseFetch = new Proxy<NoResponseFetchProxy>({} as any, {
  get(_target: unknown, method: string): any {
    if (!['get', 'post', 'put', 'delete', 'patch'].includes(method)) {
      throw new Error(`Unsupported HTTP method: ${method}`)
    }

    const httpMethod = method as 'get' | 'post' | 'put' | 'delete' | 'patch'

    const api = useApi()

    return async <T>(params: {
      url: string
      payload?: T
      success?: MaybeRefOrGetter<string | undefined>
      error?: string | ((error: any) => string | undefined)
    }): Promise<boolean> => {
      const { url, payload = {}, success: s, error: e } = params

      try {
        if (httpMethod === 'get') {
          await api[httpMethod](url)
        } else {
          await api[httpMethod](url, payload ?? {})
        }

        const success = toValue(s)
        if (success) {
          addToast(url).success(success)
        }
        return true
      } catch (exception) {
        let error
        if (typeof e === 'function') {
          error = e(exception)
        } else if (e) {
          error = e
        }

        if (error) {
          addToast(url).error(error)
        }
        return false
      }
    }
  }
})
